import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { num } from '../lib/format.js';
import type { Rights } from '../lib/session.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  Tile,
} from '../components/ui.js';
import '../screens-2a.css';

interface Counts {
  subscribed: number;
  confirmed: number;
  alerted: number;
  reminded: number;
  converted: number;
  expired: number;
  cancelled: number;
  quantity: number;
}

interface WatchdogStats {
  days: number;
  totals: Counts;
  rates: { confirmed: number | null; converted: number | null };
  races: Array<
    {
      raceId: string | null;
      name: string | null;
      tribunes: Array<{ category: string | null } & Counts>;
    } & Counts
  >;
  brands: Array<{ brand: string } & Counts>;
}

const PERIODS: Array<[number, string]> = [
  [30, '30 dní'],
  [90, '90 dní'],
  [365, '1 rok'],
];

const pct = (v: number | null): string => (v === null ? '—' : `${num(v)} %`);

function CountCells({ c }: { c: Counts }): JSX.Element {
  return (
    <>
      <td className="n">{num(c.subscribed)}</td>
      <td className="n">{num(c.confirmed)}</td>
      <td className="n">{num(c.alerted)}</td>
      <td className="n">{num(c.reminded)}</td>
      <td className="n">{num(c.converted)}</td>
      <td className="n">{num(c.expired)}</td>
      <td className="n">{num(c.quantity)}</td>
    </>
  );
}

const HEAD = (
  <>
    <th className="n">Přihlášeno</th>
    <th className="n">Potvrzeno</th>
    <th className="n">Výzva</th>
    <th className="n">Připomínka</th>
    <th className="n">Koupilo</th>
    <th className="n">Vypršelo</th>
    <th className="n" title="Součet kusů u aktivních hlídání">
      Poptávka (ks)
    </th>
  </>
);

/** Komunikace → Hlídací psi: poptávka podle závodů a tribun a konverze výzev. Jen souhrny. */
export function Watchdogs({ rights }: { rights: Rights }): JSX.Element {
  const [days, setDays] = useState(90);
  const [brand, setBrand] = useState('');
  const [granted, setGranted] = useState<number | null>(null);
  const [askGrant, setAskGrant] = useState(false);
  const client = useQueryClient();
  const grant = useMutation({
    mutationFn: (confirmation: string) =>
      api.post<{ granted: number }>('/api/watchdogs/legitimate-interest', { confirmation }),
    onSuccess: async (r) => {
      setGranted(r.granted);
      await client.invalidateQueries({ queryKey: ['watchdogs'] });
    },
  });
  const query = useQuery<WatchdogStats>({
    queryKey: ['watchdogs', days, brand],
    queryFn: () => api.get(`/api/communication/watchdogs${qs({ days, brand })}`),
  });

  if (query.isLoading) return <LoadingState what="hlídací psy" />;
  if (query.isError || !query.data)
    return (
      <ErrorState
        title="Hlídací psy se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const d = query.data;
  const t = d.totals;

  return (
    <>
      <Panel
        title="Hlídací psi"
        subtitle="Zákazníci, kteří si nechali hlídat vstupenky na vyprodaný závod, a kolik z nich po výzvě nakoupilo. E-shop nic nepočítá, počítá se tady."
      >
        <div className="s2a-filters">
          <Field label="Období">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="watchdog-period"
            >
              {PERIODS.map(([v, title]) => (
                <option key={v} value={v}>
                  Posledních {title}
                </option>
              ))}
            </select>
          </Field>
          <BrandSelect value={brand} onChange={setBrand} />
        </div>
        <div className="tiles" data-testid="watchdog-funnel">
          <Tile label="Přihlášeno" value={num(t.subscribed)} note="Hlídání vzniklá v období." />
          <Tile
            label="Potvrzeno"
            value={num(t.confirmed)}
            note={`${pct(d.rates.confirmed)} z přihlášených`}
          />
          <Tile
            label="Odeslaná výzva"
            value={num(t.alerted)}
            note="Hlídání, které dostalo „je to tady“."
          />
          <Tile label="Připomínka" value={num(t.reminded)} note="Nejvýš jedna po 48 hodinách." />
          <Tile
            label="Koupilo po výzvě"
            value={num(t.converted)}
            note={`${pct(d.rates.converted)} z výzev (token z odkazu, jinak e-mail a závod do 7 dnů)`}
          />
          <Tile label="Poptávka (ks)" value={num(t.quantity)} note="Aktivní hlídání." />
        </div>
      </Panel>

      <Panel title="Podle závodů a tribun">
        {d.races.length === 0 ? (
          <EmptyState title="V tomto období není žádné hlídání">
            Zkuste delší období nebo jinou značku. Hlídání vznikají, když si zákazník nechá hlídat
            vyprodaný závod.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table data-testid="watchdog-races">
              <thead>
                <tr>
                  <th>Závod / tribuna</th>
                  {HEAD}
                </tr>
              </thead>
              <tbody>
                {d.races.map((r) => (
                  <RaceRows key={r.raceId ?? 'bez-zavodu'} race={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {rights.write('settings') ? (
        <Panel
          title="Oprávněný zájem u historických hlídačů"
          subtitle="Hlídání převzatá z e-shopu bez dvojího potvrzení zatím nemají souhlas, proto jim nechodí výzvy."
        >
          <button className="btn dng" onClick={() => setAskGrant(true)}>
            Uplatnit oprávněný zájem u historických hlídačů
          </button>
          <p className="note">Jen po písemném schválení právníkem.</p>
          <ConfirmDialog
            open={askGrant}
            title="Uplatnit oprávněný zájem?"
            danger
            requireWord="OPRAVNENY ZAJEM"
            confirmLabel="Zapsat souhlas"
            busy={grant.isPending}
            onCancel={() => setAskGrant(false)}
            onConfirm={(word) => {
              grant.mutate(word ?? '');
              setAskGrant(false);
            }}
          >
            Zapsat všem historickým hlídačům souhlas s hlídáním? Od té chvíle jim hub začne posílat
            výzvy. Krok nejde vzít zpět.
          </ConfirmDialog>
          {granted !== null ? (
            <p className="note">Souhlas zapsán u {num(granted)} hlídačů.</p>
          ) : null}
          {grant.isError ? (
            <ErrorState title="Souhlas se nepodařilo zapsat" error={grant.error} />
          ) : null}
        </Panel>
      ) : null}

      <Panel title="Podle značky">
        {d.brands.length === 0 ? (
          <EmptyState title="Žádná značka nemá hlídání">
            Značky se objeví, jakmile v tomto období vznikne první hlídání.
          </EmptyState>
        ) : null}
        <div className="scroll">
          <table data-testid="watchdog-brands">
            <thead>
              <tr>
                <th>Značka</th>
                {HEAD}
              </tr>
            </thead>
            <tbody>
              {d.brands.map((b) => (
                <tr key={b.brand}>
                  <td>
                    <b>{b.brand}</b>
                  </td>
                  <CountCells c={b} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

function RaceRows({ race }: { race: WatchdogStats['races'][number] }): JSX.Element {
  return (
    <>
      <tr>
        <td>
          <b>{race.name ?? race.raceId ?? 'Bez závodu'}</b>
        </td>
        <CountCells c={race} />
      </tr>
      {race.tribunes.map((tr) => (
        <tr key={tr.category ?? 'cely-zavod'} className="muted">
          <td>&nbsp;&nbsp;{tr.category ?? 'celý závod'}</td>
          <CountCells c={tr} />
        </tr>
      ))}
    </>
  );
}
